import { AutoFocusExtension, ClearEditorExtension } from "@lexical/extension";
import { HistoryExtension } from "@lexical/history";
import {
  AutoLinkExtension,
  autoLinkEmailMatcher,
  createLinkMatcherWithRegExp,
  LinkExtension,
} from "@lexical/link";
import { ListExtension } from "@lexical/list";
import { $convertToMarkdownString, TRANSFORMERS } from "@lexical/markdown";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { LexicalExtensionComposer } from "@lexical/react/LexicalExtensionComposer";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextExtension } from "@lexical/rich-text";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import { CLEAR_EDITOR_COMMAND, configExtension, defineExtension, LexicalEditor } from "lexical";
import { useCreateMessageMutation } from "messaging/_api/messaging-api";
import { getMessagingErrorMessage, getMessagingFieldErrors } from "messaging/_api/messaging-error-message";
import { validateAttachment } from "messaging/_api/messaging-validation";
import {
  SEND_MESSAGE_COMMAND,
  SubmitMessagePlugin,
} from "messaging/_views/components/editor/plugins/submit-message-plugin";
import ToolbarPlugin from "messaging/_views/components/editor/plugins/toolbar-plugin";
import Attachment, { FileAttachment } from "messaging/attachments/_views/message-attachment-editor";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Forum from "messaging/forums/forum";
import Message from "messaging/message";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useSession } from "users/__session/session-context";
import "./message-editor.css";

const ActionsContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(0.5),
}));
const OptionButtons = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
}));
const OptionsButtonsContainer = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
}));
const EditorWrapper = styled("div")(() => ({
  position: "relative",
}));
const SendButtonAdornment = styled("div")(() => ({
  alignSelf: "center",
  display: "flex",
  flexShrink: 0,
}));
const SendButton = styled(LoadingButton)(({ theme }) => ({
  lineHeight: 1,
  minHeight: "32px",
  padding: theme.spacing(0.5, 0.75),
  "& .MuiButton-endIcon": {
    marginLeft: theme.spacing(0.75),
  },
}));
const AttachButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const AttachmentInput = styled("input")(() => ({
  display: "none",
}));

const PlaceholderText = () => {
  return <div className="placeholder-text">Type your message here...</div>;
};

const EditorContentEditable = ({ disabled }: { disabled: boolean }) => {
  return (
    <div className="editor-content">
      <ContentEditable
        aria-placeholder="Type your message here..."
        contentEditable={!disabled}
        placeholder={<PlaceholderText />}
      />
    </div>
  );
};

const MESSAGE_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._~#=+]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_~#?&/=+]*)/,
    (text) => (text.startsWith("http") ? text : `https://${text}`),
  ),
  autoLinkEmailMatcher,
];

const messageEditorExtension = defineExtension({
  name: "AttorneyHubMessageEditor",
  namespace: "lexical-editor",
  dependencies: [
    AutoFocusExtension,
    ClearEditorExtension,
    HistoryExtension,
    RichTextExtension,
    ListExtension,
    LinkExtension,
    configExtension(AutoLinkExtension, { matchers: MESSAGE_LINK_MATCHERS }),
  ],
  theme: {
    root: "editor-root",
    quote: "editor-quote",
    link: "editor-link",
    text: {
      bold: "editor-textBold",
      code: "editor-textCode",
      italic: "editor-textItalic",
      strikethrough: "editor-textStrikethrough",
      subscript: "editor-textSubscript",
      superscript: "editor-textSuperscript",
      underline: "editor-textUnderline",
      underlineStrikethrough: "editor-textUnderlineStrikethrough",
    },
  },
  onError: () => undefined,
});

type MessageEditorProps = {
  forum: Forum | undefined;
  autoFocus?: boolean;
  locked?: boolean;
  onFormattingToggled: (enabled: boolean) => void;
  onAttachmentAdded: (attachment: File) => void;
  onMessageSent: (message: Message) => void;
};

export default function MessageEditor(props: Readonly<MessageEditorProps>) {
  const [isSending, setIsSending] = React.useState(false);
  const [messageText, setMessageText] = React.useState<string>("");
  const [subject, setSubject] = React.useState<string | undefined>("");
  const [attachment, setAttachment] = React.useState<FileAttachment | undefined>();
  const [fieldError, setFieldError] = React.useState<string | undefined>();

  const [showToolbar, setShowToolbar] = React.useState(false);
  const editorRef = React.useRef<LexicalEditor>(null);
  const hiddenAttachmentFileInput = React.useRef<HTMLInputElement>(null);
  const session = useSession();
  const [createMessage] = useCreateMessageMutation();
  const isDisabled = (): boolean => {
    return props.locked || !props.forum || isSending || !session.user;
  };

  const hasVisibleMessageText = (): boolean => /\S/.test(messageText);
  const hasAttachments = (): boolean => attachment !== undefined;
  const messageIsEmpty = (): boolean => !hasVisibleMessageText() && !hasAttachments();
  const canSendMessage = (): boolean => {
    return !isDisabled() && !messageIsEmpty() && !fieldError;
  };
  const emptyMessageTooltip = !isDisabled() && messageIsEmpty() ? "Cannot send an empty message" : "";

  const sendMessage = async (message: Message, editor?: LexicalEditor) => {
    if (!canSendMessage()) return;

    setIsSending(true);
    try {
      message.publish();
      const createdMessage = await createMessage({ message }).unwrap();
      props.onMessageSent(createdMessage);
      editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      setMessageText("");
      setSubject(undefined);
      setAttachment(undefined);
    } catch (error) {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.contentPlainText ?? fieldErrors.attachments);
      enqueueSnackbar(getMessagingErrorMessage(error, "Failed to send message. Please try again."), {
        variant: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const draftMessage = (messageBody?: string) => {
    if (!props.forum || !session.user || !canSendMessage()) return;
    const attachmentList = attachment ? [attachment.file] : [];
    try {
      return Message.draft({
        id: Guid.generate(),
        created: new AHDate(),
        forum: props.forum,
        sender: session.user,
        content: messageBody ?? messageText,
        subject: subject,
        attachments: attachmentList,
      });
    } catch {
      setFieldError("We could not prepare this message. Check the message and attachment.");
    }
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const selectedFile = files[0];
    const attachmentError = validateAttachment(selectedFile);
    if (attachmentError) {
      setFieldError(attachmentError);
      event.target.value = "";
      return;
    }

    setAttachment({
      file: selectedFile,
      fileType: selectedFile.type,
      url: URL.createObjectURL(selectedFile),
    });
    setFieldError(undefined);

    event.target.value = "";
    props?.onAttachmentAdded(selectedFile);
  };

  const handleSendButtonClick = () => {
    if (!canSendMessage()) return;
    const draft = draftMessage();
    if (!draft) return;
    editorRef.current?.dispatchCommand(SEND_MESSAGE_COMMAND, draft);
  };

  const constructMessage = (): Message | undefined => {
    if (messageIsEmpty() || !session.user?.id || !props.forum) return;
    const message = Message.create({
      id: Guid.generate(),
      forum: props.forum,
      content: messageText,
      senderId: session.user.id,
      subject: subject,
    });

    if (!attachment) return message;
    const attachmentFile = new MessageFileInfo({
      id: Guid.generate(),
      name: attachment.file.name,
      fileSize: attachment.file.size,
      mimeType: attachment.fileType,
      createdDate: new AHDate(),
      rawData: attachment.file,
      dataUri: attachment.url,
    });
    message.addAttachments([attachmentFile]);

    return message;
  };

  return (
    <React.Fragment>
      <AttachmentInput
        type="file"
        hidden={true}
        multiple={false}
        ref={hiddenAttachmentFileInput}
        accept={Config.acceptedAttachmentFormats.join(", ")}
        onChange={handleAttachmentUpload}
        style={{ display: "none" }}
      />
      <EditorWrapper>
        <LexicalExtensionComposer extension={messageEditorExtension} contentEditable={null}>
          <div className="editor-container">
            {showToolbar && <ToolbarPlugin />}
            <div className="editor-inner">
              <div className="editor-body">
                <EditorContentEditable disabled={isDisabled()} />
                <SubmitMessagePlugin
                  message={constructMessage()}
                  listenForEnterKey={!showToolbar && canSendMessage()}
                  listenForSubmitButton={canSendMessage()}
                  onSend={sendMessage}
                />
                <EditorRefPlugin editorRef={editorRef} />
                <OnChangePlugin
                  ignoreSelectionChange={true}
                  onChange={(editorState) => {
                    editorState.read(() => {
                      const markdown = $convertToMarkdownString(TRANSFORMERS);
                      setMessageText(markdown);
                      setFieldError(undefined);
                    });
                  }}
                />
              </div>
              <SendButtonAdornment>
                <Tooltip title={emptyMessageTooltip}>
                  <span>
                    <SendButton
                      color="primary"
                      size="medium"
                      endIcon={<SendIcon fontSize="medium" />}
                      loading={isSending}
                      disabled={!canSendMessage()}
                      onClick={handleSendButtonClick}
                    >
                      {`Send${isSending ? "ing..." : ""}`}
                    </SendButton>
                  </span>
                </Tooltip>
              </SendButtonAdornment>
            </div>
          </div>
        </LexicalExtensionComposer>
      </EditorWrapper>
      <ActionsContainer>
        <OptionButtons>
          <OptionsButtonsContainer>
            <Tooltip title="Toggle Formatting">
              <span>
                <ToggleButton
                  disabled={isDisabled()}
                  size="small"
                  selected={showToolbar}
                  style={{ border: 0 }}
                  value={showToolbar}
                  onChange={async () => {
                    const nextShowToolbar = !showToolbar;
                    setShowToolbar(nextShowToolbar);
                    props.onFormattingToggled(nextShowToolbar);
                  }}
                >
                  <TextFormatIcon />
                </ToggleButton>
              </span>
            </Tooltip>
            <Tooltip title="Attach File">
              <span>
                <AttachButton
                  size="small"
                  disabled={attachment !== undefined || isDisabled()}
                  onClick={() => hiddenAttachmentFileInput?.current?.click()}
                >
                  <AttachFileIcon />
                </AttachButton>
              </span>
            </Tooltip>
            {attachment?.url && (
              <Attachment attachment={attachment} onAttachmentRemoved={() => setAttachment(undefined)} />
            )}
            {fieldError && <div role="alert" className="editor-field-error">{fieldError}</div>}
          </OptionsButtonsContainer>
        </OptionButtons>
      </ActionsContainer>
    </React.Fragment>
  );
}
